import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layouts/AdminLayout';
import DashboardOverviewPrincipal from './principal/DashboardOverview';
import ContentApprovalsTab from './principal/ContentApprovals';
import SubmissionReviewsTab from './principal/SubmissionReviews';
import ActivityHistoryTab from './principal/ActivityHistory';
import '../styles/dashboard.css';
import './principal/PrincipalDashboard.css';

/**
 * PrincipalDashboard Component
 * 
 * Main dashboard for Principal/Content Approver role
 * - Review and approve/reject content changes
 * - Review and approve/reject submissions (testimonials, etc)
 * - View approval activity history
 * - Statistics on pending and completed approvals
 * 
 * Theme: Orange (#f59e0b)
 */
function PrincipalDashboard({ user, onLogout, apiBase }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    pendingContent: 0,
    pendingSubmissions: 0,
    approvedThisMonth: 0,
    rejectedThisMonth: 0
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadStats();
  }, [refreshTrigger]);

  const loadStats = async () => {
    try {
      // Fetch pending content count
      const contentResponse = await fetch(`${apiBase}/approver/pending-content?limit=1`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const contentData = await contentResponse.json();
      
      // Fetch pending submissions count
      const submissionsResponse = await fetch(`${apiBase}/approver/pending-submissions?limit=1`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const submissionsData = await submissionsResponse.json();

      if (contentData.ok && submissionsData.ok) {
        setStats(prev => ({
          ...prev,
          pendingContent: contentData.data?.pagination?.total || 0,
          pendingSubmissions: submissionsData.data?.pagination?.total || 0
        }));
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="12 3 20 7.5 20 16.5 12 21 4 16.5 4 7.5 12 3" />
          <polyline points="12 12 20 7.5" />
          <polyline points="12 12 12 21" />
          <polyline points="12 12 4 7.5" />
        </svg>
      )
    },
    {
      id: 'content-approvals',
      label: 'Persetujuan Konten',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'submission-reviews',
      label: 'Review Pengajuan',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'activity-history',
      label: 'Riwayat Aktivitas',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      )
    }
  ];

  const roleColor = '#f59e0b';
  const dashboardTitle = 'Principal Dashboard';

  return (
    <AdminLayout
      user={user}
      onLogout={onLogout}
      roleColor={roleColor}
      dashboardTitle={dashboardTitle}
      menuItems={menuItems}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="dashboard-content">
        {activeTab === 'dashboard' && (
          <DashboardOverviewPrincipal 
            apiBase={apiBase} 
            stats={stats} 
            onRefresh={handleRefresh}
            roleColor={roleColor}
          />
        )}

        {activeTab === 'content-approvals' && (
          <ContentApprovalsTab 
            apiBase={apiBase} 
            roleColor={roleColor}
            onApprovalChange={handleRefresh}
          />
        )}

        {activeTab === 'submission-reviews' && (
          <SubmissionReviewsTab 
            apiBase={apiBase} 
            roleColor={roleColor}
            onReviewChange={handleRefresh}
          />
        )}

        {activeTab === 'activity-history' && (
          <ActivityHistoryTab 
            apiBase={apiBase} 
            roleColor={roleColor}
          />
        )}
      </div>
    </AdminLayout>
  );
}

export default PrincipalDashboard;
